import type { JwtPayload } from '$utils/jwt'
import type { DashboardExportRequest, DashCardParams } from './DashboardPage.model'
import utc from 'dayjs/plugin/utc'
import { replaceUrl } from '$utils/misc'
import dayjs from 'dayjs'
import {
	convertUnixToLocale,
	convertUnixToUtcEnd,
	convertUnixToUtcStart,
	type UnixDateRange
} from '$types/datepicker'
import { addNotification } from '../stores'
import { NotificationType } from '$types/notification'
import { fetchUtils } from '$utils/fetchUtils'
// register plugins
dayjs.extend(utc)

const ERROR_NOTIFICATION_TIMEOUT = 7000

export function extractUsernameFromJwt(jwt: JwtPayload): string | null {
	if (jwt?.ufirstname && jwt?.ulastname) {
		return `${jwt.ufirstname} ${jwt.ulastname}`
	} else {
		return null
	}
}

export function fillCardParamsFromUrlOrDefault(urlParams: URLSearchParams): DashCardParams {
	const mainFrom = dayjs().utc().startOf('month').unix()
	const mainTo = dayjs().utc().endOf('day').unix()

	// check, if the params exist, is not 0 and not 'NaN'
	const mainFromParam =
		urlParams.get('mainFrom') && !isNaN(Number(urlParams.get('mainFrom')))
			? Number(urlParams.get('mainFrom'))
			: null
	const mainToParam =
		urlParams.get('mainTo') && !isNaN(Number(urlParams.get('mainTo')))
			? Number(urlParams.get('mainTo'))
			: null
	const prevFromParam =
		urlParams.get('prevFrom') && !isNaN(Number(urlParams.get('prevFrom')))
			? Number(urlParams.get('prevFrom'))
			: null
	const prevToParam =
		urlParams.get('prevTo') && !isNaN(Number(urlParams.get('prevTo')))
			? Number(urlParams.get('prevTo'))
			: null
	return {
		mainTimeframe: [
			// we must convert to the locale time such that the date picker works correctly,
			// since the timestamps in the url are utc unix timestamps
			mainFromParam
				? convertUnixToLocale(mainFromParam).unix()
				: convertUnixToLocale(mainFrom).unix(),
			mainToParam ? convertUnixToLocale(mainToParam).unix() : convertUnixToLocale(mainTo).unix()
		],
		prevTimeframe: [
			// we must convert to the locale time such that the date picker works correctly,
			// since the timestamps in the url are utc unix timestamps
			prevFromParam
				? convertUnixToLocale(prevFromParam).unix()
				: dayjs().subtract(1, 'month').startOf('month').unix(),
			prevToParam
				? convertUnixToLocale(prevToParam).unix()
				: dayjs().subtract(1, 'month').endOf('month').unix()
		],
		userId: urlParams.getAll('userId')?.length > 0 ? urlParams.getAll('userId') : [] //constant id to select all persons of a tenant
	}
}

export function updateUrlWithSearchParams(
	pageId: string | null,
	cardParams: DashCardParams,
	window: Window
) {
	const searchParams = new URLSearchParams(window.location.search)
	// conversion of unix to utc timezone unix
	const mainFrom = convertUnixToUtcStart(cardParams.mainTimeframe[0]).unix()
	const mainTo = convertUnixToUtcEnd(cardParams.mainTimeframe[1]).unix()
	const prevFrom = convertUnixToUtcStart(cardParams.prevTimeframe[0]).unix()
	const prevTo = convertUnixToUtcEnd(cardParams.prevTimeframe[1]).unix()

	if (pageId) {
		searchParams.set('pageId', pageId)
	}
	if (cardParams?.mainTimeframe) {
		searchParams.set('mainFrom', mainFrom.toString())
		searchParams.set('mainTo', mainTo.toString())
	}
	if (cardParams?.prevTimeframe) {
		searchParams.set('prevFrom', prevFrom.toString())
		searchParams.set('prevTo', prevTo.toString())
	}
	if (cardParams?.userId) {
		searchParams.delete('userId')
		cardParams.userId.forEach((userId) => {
			searchParams.append('userId', userId)
		})
	}
	replaceUrl('/dashboard', window, searchParams)
}

export async function exportDashboardToExcel(
	pageId: string | null,
	userId: string[] | null,
	mainTimeframe: UnixDateRange,
	prevTimeframe: UnixDateRange
) {
	if (!pageId) {
		addNotification({
			message: 'Bitte wähle eine Seite für den Export aus!',
			type: NotificationType.ERROR,
			dismissible: true,
			timeout: ERROR_NOTIFICATION_TIMEOUT
		})
		return
	}
	if (!userId) {
		addNotification({
			message: 'Bitte wähle einen Nutzer für den Export aus!',
			type: NotificationType.ERROR,
			dismissible: true,
			timeout: ERROR_NOTIFICATION_TIMEOUT
		})
		return
	}
	if (!mainTimeframe || mainTimeframe.length !== 2) {
		addNotification({
			message: 'Bitte wähle einen Hauptzeitraum für den Export aus!',
			type: NotificationType.ERROR,
			dismissible: true,
			timeout: ERROR_NOTIFICATION_TIMEOUT
		})
		return
	}
	if (!prevTimeframe || prevTimeframe.length !== 2) {
		addNotification({
			message: 'Bitte wähle einen Vergleichszeitraum für den Export aus!',
			type: NotificationType.ERROR,
			dismissible: true,
			timeout: ERROR_NOTIFICATION_TIMEOUT
		})
		return
	}

	// when we sort the two values, we can easily access the values without checking for the smaller/bigger value
	mainTimeframe = [...mainTimeframe.sort((a: number, b: number) => a - b)]
	prevTimeframe = [...prevTimeframe.sort((a: number, b: number) => a - b)]

	const payload: DashboardExportRequest = {
		pageId,
		userId,
		main: {
			from: convertUnixToUtcStart(mainTimeframe[0]).unix(),
			to: convertUnixToUtcEnd(mainTimeframe[1]).unix()
		},
		comp: {
			from: convertUnixToUtcStart(prevTimeframe[0]).unix(),
			to: convertUnixToUtcEnd(prevTimeframe[1]).unix()
		}
	}

	await fetchUtils.postAndDownload('/api/dashboard/export', payload, 'dashboard.xlsx').then(() =>
		addNotification({
			message: 'Dashboard wurde erfolgreich exportiert!',
			type: NotificationType.SUCCESS,
			dismissible: false
		})
	)
}
